import { useMemo } from 'react';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, ManagedDataTable } from '@/design-system';
import type { WhatsAppServer } from '../api/whatsapp-servers.service';
import { getServerStatusColor, getServerStatusText } from '../utils/server-status';

export interface WhatsAppServerTableHandlers {
  onEdit: (server: WhatsAppServer) => void;
  onDelete: (server: WhatsAppServer) => void;
  onCreate: () => void;
}

interface WhatsAppServerTableProps {
  servers: WhatsAppServer[];
  loading: boolean;
  handlers: WhatsAppServerTableHandlers;
}

export function WhatsAppServerTable({
  servers,
  loading,
  handlers,
}: WhatsAppServerTableProps) {
  const columns = useMemo<ColumnDef<WhatsAppServer>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
            {row.original.notes && <div className="text-xs text-gray-500 mt-1">{row.original.notes}</div>}
          </div>
        ),
      },
      {
        accessorKey: 'baseUrl',
        header: 'URL',
        minSize: 320,
        cell: ({ row }) => <div className="whitespace-nowrap text-sm text-gray-900 font-mono">{row.original.baseUrl}</div>,
      },
      {
        id: 'capacity',
        header: 'Capacidade',
        cell: ({ row }) => {
          const utilizationRate = row.original.maxCapacity > 0 ? Math.round((row.original.currentLoad / row.original.maxCapacity) * 100) : 0;
          return (
            <div>
              <div className="text-sm text-gray-900">
                {row.original.currentLoad} / {row.original.maxCapacity}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div
                  className={`h-1.5 rounded-full ${
                    utilizationRate >= 90 ? 'bg-red-500' : utilizationRate >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        id: 'utilization',
        header: 'Utilização',
        cell: ({ row }) => {
          const utilizationRate = row.original.maxCapacity > 0 ? Math.round((row.original.currentLoad / row.original.maxCapacity) * 100) : 0;
          return <div className="text-sm font-medium text-gray-900">{utilizationRate}%</div>;
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getServerStatusColor(row.original)}`}>
            {getServerStatusText(row.original)}
          </span>
        ),
      },
      {
        accessorKey: 'priority',
        header: 'Prioridade',
        cell: ({ row }) => <div className="text-sm text-gray-500">{row.original.priority}</div>,
      },
      {
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm"
              type="button"
              onClick={() => handlers.onEdit(row.original)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Editar"
              aria-label="Editar"
            >
              <Edit size={16} />
            </Button>
            <Button variant="ghost" size="sm"
              type="button"
              onClick={() => handlers.onDelete(row.original)}
              className="text-gray-400 hover:text-red-600 transition-colors"
              title="Deletar"
              aria-label="Deletar"
              disabled={row.original.currentLoad > 0}
            >
              <Trash2 size={16} className={row.original.currentLoad > 0 ? 'opacity-50' : ''} />
            </Button>
          </div>
        ),
      },
    ],
    [handlers]
  );

  return (
    <ManagedDataTable
      columns={columns}
      data={servers}
      loading={loading}
      title="Lista de servidores"
      searchPlaceholder="Buscar servidor..."
      toolbarButtons={[
        {
          label: 'Novo servidor',
          icon: <Plus size={16} />,
          onClick: handlers.onCreate,
        },
      ]}
      clientPagination
      emptyMessage="Nenhum servidor encontrado."
    />
  );
}
